const posterNeedsCapture = (poster) =>
	poster &&
	poster.dataset.posterReady !== "true" &&
	!poster.getAttribute("src");

const captureFirstFrame = (video, poster) => {
	if (!posterNeedsCapture(poster)) {
		return;
	}

	let captured = false;

	const draw = () => {
		if (captured || !video.videoWidth || !video.videoHeight) {
			return;
		}

		try {
			const canvas = document.createElement("canvas");
			canvas.width = video.videoWidth;
			canvas.height = video.videoHeight;
			canvas.getContext("2d").drawImage(video, 0, 0, canvas.width, canvas.height);
			poster.src = canvas.toDataURL("image/jpeg", 0.85);
			poster.hidden = false;
			poster.dataset.posterReady = "true";
			captured = true;
		} catch {
			// Cross-origin or tainted canvas — keep manual poster if present.
		}
	};

	const seekAndDraw = () => {
		const onSeeked = () => {
			video.removeEventListener("seeked", onSeeked);
			draw();
		};

		video.addEventListener("seeked", onSeeked, { once: true });

		const target =
			Number.isFinite(video.duration) && video.duration > 0
				? Math.min(0.1, video.duration / 10)
				: 0.001;

		try {
			video.currentTime = target;
		} catch {
			draw();
		}
	};

	const prepareFrame = () => {
		if (video.readyState >= HTMLMediaElement.HAVE_CURRENT_DATA) {
			draw();
		}

		if (!captured) {
			seekAndDraw();
		}
	};

	if (video.readyState >= HTMLMediaElement.HAVE_METADATA) {
		prepareFrame();
		return;
	}

	video.addEventListener("loadedmetadata", prepareFrame, { once: true });
};

const setupVideoPoster = (media) => {
	const video = media.querySelector(".agent-features__video");
	const poster = media.querySelector(".agent-features__poster[data-poster-auto]");

	if (!video || !posterNeedsCapture(poster)) {
		return;
	}

	video.preload = "auto";

	if (video.readyState >= HTMLMediaElement.HAVE_METADATA) {
		captureFirstFrame(video, poster);
		return;
	}

	const startCapture = () => {
		captureFirstFrame(video, poster);
	};

	video.addEventListener("loadedmetadata", startCapture, { once: true });

	if (video.networkState === HTMLMediaElement.NETWORK_EMPTY) {
		video.load();
	}
};

const pauseVideos = (root) => {
	root.querySelectorAll(".agent-features__video").forEach((video) => {
		video.pause();
		video.currentTime = 0;
		video.controls = false;
	});

	root.querySelectorAll(".agent-features__media.is-playing").forEach((media) => {
		media.classList.remove("is-playing");
	});
};

const moveAccent = (cards, nextIndex) => {
	cards.forEach((card, index) => {
		const accent = card.querySelector(".agent-features__accent");

		if (index === nextIndex) {
			if (!accent) {
				const span = document.createElement("span");
				span.className = "agent-features__accent agent-features__accent--tl";
				span.setAttribute("aria-hidden", "true");
				card.insertBefore(span, card.firstChild);
			}
			return;
		}

		accent?.remove();
	});
};

const setPanelState = (cards, panels, nextIndex) => {
	cards.forEach((card, index) => {
		const selected = index === nextIndex;

		card.classList.toggle("is-active", selected);
		card.setAttribute("aria-selected", selected ? "true" : "false");
		card.tabIndex = selected ? 0 : -1;
	});

	panels.forEach((panel, index) => {
		const selected = index === nextIndex;

		panel.classList.toggle("is-active", selected);
		panel.setAttribute("aria-hidden", selected ? "false" : "true");
		panel.hidden = !selected;
	});

	moveAccent(cards, nextIndex);
};

const activateCard = (section, nextCard) => {
	const cards = [...section.querySelectorAll('.agent-features__card[role="tab"]')];
	const panels = [...section.querySelectorAll(".agent-features__media-panel")];
	const nextIndex = cards.indexOf(nextCard);

	if (nextIndex < 0 || nextCard.classList.contains("is-active")) {
		return;
	}

	pauseVideos(section);
	setPanelState(cards, panels, nextIndex);
};

const initSection = (section) => {
	const cards = [...section.querySelectorAll('.agent-features__card[role="tab"]')];

	section.querySelectorAll(".agent-features__media.has-video").forEach(setupVideoPoster);

	if (!cards.length) {
		section.querySelectorAll(".agent-features__play").forEach((button) => {
			button.addEventListener("click", () => {
				const media = button.closest(".agent-features__media");
				const video = media?.querySelector("video");

				if (!media || !video) {
					return;
				}

				media.classList.add("is-playing");
				video.controls = true;
				video.play().catch(() => {});
			});
		});
		return;
	}

	cards.forEach((card) => {
		card.addEventListener("click", () => {
			if (!card.classList.contains("is-active")) {
				activateCard(section, card);
			}
		});

		card.addEventListener("keydown", (event) => {
			const current = cards.indexOf(card);
			let next = current;

			if (event.key === "ArrowDown" || event.key === "ArrowRight") {
				next = (current + 1) % cards.length;
			} else if (event.key === "ArrowUp" || event.key === "ArrowLeft") {
				next = (current - 1 + cards.length) % cards.length;
			} else if (event.key === "Home") {
				next = 0;
			} else if (event.key === "End") {
				next = cards.length - 1;
			} else {
				return;
			}

			event.preventDefault();
			activateCard(section, cards[next]);
			cards[next].focus();
		});
	});

	section.querySelectorAll(".agent-features__play").forEach((button) => {
		button.addEventListener("click", (event) => {
			event.stopPropagation();

			const media = button.closest(".agent-features__media");
			const video = media?.querySelector("video");

			if (!media || !video) {
				return;
			}

			media.classList.add("is-playing");
			video.controls = true;
			video.play().catch(() => {});
		});
	});
};

const init = () => {
	document.querySelectorAll(".agent-features").forEach(initSection);
};

if (document.readyState === "loading") {
	document.addEventListener("DOMContentLoaded", init);
} else {
	init();
}
